// App embed mode: the widget running full screen inside a mobile app WebView
// (spec F22, docs/livechat-mobile-embed.md).
//
// Launch URL: app.html?embed=app&channel=<identifier>&lang=<pt|es|en>#identity_token=<jwt>
// - The identity token travels in the fragment, which browsers never send to a
//   server, and is removed from the address bar as soon as it is read.
// - It is sent once, with the message that starts the conversation, and then
//   forgotten; a refused token is reported to the app as `identity_error`.
// - Events for the app go through whichever native bridge exists: React Native
//   (`window.ReactNativeWebView`), iOS WKWebView (`webkit.messageHandlers.ituranChat`)
//   or an Android JavascriptInterface named `IturanChatAndroid`. No message
//   content is ever posted to the host: only what it needs for a badge.

export const BRIDGE_NAME = 'ituranChat';
export const ANDROID_INTERFACE = 'IturanChatAndroid';
export const EVENT_SOURCE = 'ituran-livechat';

export function readLaunch(location) {
  const params = new URLSearchParams(location?.search || '');
  const fragment = new URLSearchParams((location?.hash || '').replace(/^#/, ''));
  const token = fragment.get('identity_token');
  return {
    active: params.get('embed') === 'app',
    channel: params.get('channel') || null,
    language: params.get('lang') || null,
    identityToken: token && token.trim() ? token.trim() : null,
  };
}

export function scrubIdentityFromUrl(win) {
  const { location, history } = win;
  if (!location?.hash || !location.hash.includes('identity_token')) return false;
  try {
    history.replaceState(history.state, '', `${location.pathname}${location.search}`);
    return true;
  } catch {
    return false;
  }
}

export function postToHost(win, type, detail = {}) {
  const message = { source: EVENT_SOURCE, type, ...detail };
  const serialized = JSON.stringify(message);
  try {
    if (win.ReactNativeWebView && typeof win.ReactNativeWebView.postMessage === 'function') {
      win.ReactNativeWebView.postMessage(serialized);
      return 'react-native';
    }
    const ios = win.webkit?.messageHandlers?.[BRIDGE_NAME];
    if (ios && typeof ios.postMessage === 'function') {
      ios.postMessage(message);
      return 'ios';
    }
    const android = win[ANDROID_INTERFACE];
    if (android && typeof android.postMessage === 'function') {
      android.postMessage(serialized);
      return 'android';
    }
  } catch {
    // A broken bridge must never break the chat itself.
  }
  return null;
}

// Error the widget shows as-is (its catch prefers `userMessage`).
export class IdentityRefusedError extends Error {
  constructor(userMessage) {
    super('identity_refused');
    this.userMessage = userMessage;
  }
}

export function createAppEmbed(win, { translate = (key) => key } = {}) {
  const launch = readLaunch(win.location);
  let identityToken = launch.identityToken;
  scrubIdentityFromUrl(win);

  const notify = (type, detail) => postToHost(win, type, detail);

  return {
    active: launch.active,
    channel: launch.channel,
    language: launch.language,

    // Token to attach to a request that starts a conversation, if any.
    identityFor(conversationId) {
      return conversationId ? null : identityToken;
    },

    setIdentityToken(token) {
      identityToken = typeof token === 'string' && token.trim() ? token.trim() : null;
    },

    // Reads the inbound response of a request that carried the token. The
    // token is spent either way: accepted tokens are single use and refused
    // ones will not become valid.
    async settleIdentity(response) {
      identityToken = null;
      if (response.status === 401) {
        notify('identity_error', { status: 401 });
        throw new IdentityRefusedError(translate('sessionExpired'));
      }
      const data = await response.json();
      if (data && data.conversation_id) {
        notify('conversation', { conversation_id: String(data.conversation_id), identified: true });
      }
      return data;
    },

    messageReceived(interaction, conversationId) {
      const role = interaction?.role === 'user' ? 'agent' : interaction?.role;
      if (role !== 'agent' && role !== 'bot') return null;
      return notify('message', {
        role,
        conversation_id: conversationId ? String(conversationId) : null,
        created_at: interaction.created_at || null,
      });
    },

    notify,
  };
}

// Called by bundle-embed.js once the template is in the DOM, before the widget
// code runs. Inert on ordinary pages apart from exposing the object.
export function installAppEmbed(win) {
  const embed = createAppEmbed(win, { translate: (key) => win.IturanI18N?.t(key) || key });
  win.IturanAppEmbed = embed;
  if (embed.active) {
    win.document?.getElementById('ituran-widget')?.classList.add('ituran-embed-app');
  }
  return embed;
}
